<template lang="pug">
FetchErrorTransition(:isFetching="isFetching" :error="error" :parsedException="parsedException")
  ShopSettingsPage(
    :shopSettings="shopSettings"
    :resourceFilters="resourceFilters"
    @save="save"
    :isSaving="isSaving"
    :enableMultilanguage="enableMultilanguage"
    )
</template>

<script>
import { mapState } from 'vuex'
import ShopSettingsPage from '@v/settings/application/shop/ShopSettingsPage.vue'
import tryCall from '@v/mixins/tryCall.js'
import { isEmpty, clone } from 'ramda'
import slugify from '@/helpers/slugify.js'
import {
  checkIfDuplicateExists,
  getDuplicatesFromArray,
} from '@/helpers/duplicatesInArrayHelper.js'
import {
  removeIncompleteThemeTranslations,
  removeIncompleteFilterTranslations,
} from '@v/util/removeIncompleteFiltersThemes'
import { parseAxiosException } from '@/helpers/exceptionParser.js'

export default {
  components: { ShopSettingsPage },
  mixins: [tryCall],
  props: {
    enableMultilanguage: Boolean, // feature flag
    activityFiltersUseSlug: Boolean, // feature flag
  },
  data() {
    return {
      shopSettings: null,
      activities: null,
      resourceFilters: null,
      savePending: false,
      isFetching: false,
      error: '',
      parsedException: null,
      isSaving: false,
    }
  },
  beforeMount() {
    this.getShopSettingsData()
  },
  computed: {
    ...mapState('imagePicker', ['file', 'removeExistingFile']),
    hasMultipleLanguages() {
      return (
        this.enableMultilanguage && !!this.shopSettings.TranslationInfo?.AvailableCultures?.length
      )
    },
  },
  methods: {
    async getShopSettingsData() {
      this.isFetching = true
      try {
        const response = await this.$apiClient.get('settings/application/shop')
        this.shopSettings = response.data.item
        this.activities = response.data.activities
        this.resourceFilters = this.buildResourceFilters({
          allActivities: this.activities,
          selectedActivities: this.shopSettings.ActivityFilters,
        })

        this.shopSettings.Theme = this.parseThemeData(this.shopSettings.Theme)
        this.shopSettings.Filters = this.parseFiltersData(this.shopSettings.Filters)
      } catch (e) {
        this.error = e
        const message = 'There was a problem: ' + e.message
        this.$notifyError(message)
        const parsedException = parseAxiosException(e)
        this.$logError(`Error expected to originate from ${parsedException.origin}`)
        this.$logError(parsedException.data)
        this.parsedException = parsedException
      }
      this.isFetching = false
    },
    async save() {
      this.isSaving = true
      this.setSlugsForThemes()
      this.setSlugsForFilters()
      const duplicateNamesFor = this.areAnyDuplicateSlugsInFiltersOrThemes()
      if (duplicateNamesFor) {
        this.$notifyError(`There are duplicate names for ${duplicateNamesFor}`)
        this.isSaving = false
        return
      }
      const dataToSave = {
        Theme: this.buildThemeForSave(),
        Filters: this.buildFiltersForSave(),
        ActivityFilters: this.buildActivityFiltersForSave(),
        TranslationInfo: this.hasMultipleLanguages ? this.buildTranslationInfoForSave() : null,
      }
      const saveShopResult = await this.saveShopSettingsData(dataToSave)
      await this.getShopSettingsData()
      const success = saveShopResult.success
      if (success) {
        this.$notifySuccess('Settings saved')
      }
      this.isSaving = false
    },
    async saveShopSettingsData(shopSettings) {
      return await this.tryCall(
        async () => await this.$apiClient.post('settings/application/shop', shopSettings),
        'Could not save settings!',
      )
    },
    buildResourceFilters({ allActivities, selectedActivities }) {
      const resourceFilters = {}
      for (const [key, value] of Object.entries(allActivities)) {
        if (value.IsActive)
          resourceFilters[key] = {
            value: selectedActivities !== null && selectedActivities[key] !== undefined,
            label: value.Description,
            slug: value.Slug,
            childResources: value.ChildResources,
            parentResourceKey: value.ParentResourceKey,
          }
      }
      return resourceFilters
    },
    buildActivityFiltersForSave() {
      const buildActivityFilters = {}
      for (const [key, activityFilter] of Object.entries(this.resourceFilters)) {
        if (activityFilter.value)
          buildActivityFilters[key] = this.activityFiltersUseSlug
            ? activityFilter.slug
            : activityFilter.label
      }

      return !isEmpty(buildActivityFilters) ? buildActivityFilters : null
    },
    parseThemeData(theme) {
      const themeParsed = { ...theme }
      for (const [key, value] of Object.entries(themeParsed)) {
        themeParsed[key] = {
          active: value.active === true || value.active === 'true',
          name: value.name,
          slug: value.slug,
        }
      }
      return themeParsed
    },
    parseFiltersData(filters) {
      return filters !== null ? filters : {}
    },
    buildThemeForSave() {
      const themeForSave = this.parseThemeForSave(this.shopSettings.Theme)
      return !isEmpty(themeForSave) ? themeForSave : null
    },
    buildFiltersForSave() {
      if (isEmpty(this.shopSettings.Filters)) return null
      return this.parseFiltersForSave(this.shopSettings.Filters)
    },
    buildTranslationInfoForSave() {
      const self = this
      let translationInfo = clone(this.shopSettings.TranslationInfo)
      translationInfo.AvailableCultures.forEach(function(availableCulture) {
        //build the theme for save
        const themeForSave = self.parseThemeForSave(
          self.shopSettings.TranslationInfo.Translations[availableCulture.Name].Theme,
        )
        translationInfo.Translations[availableCulture.Name].Theme = !isEmpty(themeForSave)
          ? themeForSave
          : null

        //build the filters for save
        const filtersForSave = self.parseFiltersForSave(
          self.shopSettings.TranslationInfo.Translations[availableCulture.Name].Filters,
        )
        translationInfo.Translations[availableCulture.Name].Filters = !isEmpty(filtersForSave)
          ? filtersForSave
          : null
      })
      translationInfo = this.clearTranslationsOfThemeAndFiltersForInactiveLanguages(translationInfo)
      // remove incomplete translations of filters and themes, to fix APP-2731
      translationInfo.Translations = removeIncompleteThemeTranslations(translationInfo.Translations)
      translationInfo.Translations = removeIncompleteFilterTranslations(
        translationInfo.Translations,
      )
      return translationInfo
    },
    clearTranslationsOfThemeAndFiltersForInactiveLanguages(translationInfo) {
      const translationInfoObject = clone(translationInfo)
      for (const languageKey of Object.keys(translationInfoObject.Translations)) {
        const languageIsActive = translationInfoObject.AvailableCultures.some(
          culture => culture.Name === languageKey,
        )

        if (!languageIsActive) {
          translationInfoObject.Translations[languageKey].Theme = null
          translationInfoObject.Translations[languageKey].Filters = null
        }
      }

      return translationInfoObject
    },
    parseThemeForSave(theme) {
      if (theme === null) return theme
      const themeForSave = clone(theme)
      for (const [key, value] of Object.entries(themeForSave)) {
        if (key.includes('new-')) {
          themeForSave[key.substr(4)] = {
            active: value.active,
            name: value.name,
            slug: value.slug,
          }
          delete themeForSave[key]
        }
      }
      return themeForSave
    },
    parseFiltersForSave(filters) {
      const filtersForSave = clone(filters)
      for (const [filterKey, filter] of Object.entries(filtersForSave)) {
        for (const [rowKey, filterRow] of Object.entries(filter.rows)) {
          if (rowKey.includes('new-')) {
            filtersForSave[filterKey].rows[rowKey.substr(4)] = {
              active: filterRow.active,
              name: filterRow.name,
              slug: filterRow.slug,
            }
            delete filtersForSave[filterKey].rows[rowKey]
          }
        }
      }
      return filtersForSave
    },
    areAnyDuplicateSlugsInFiltersOrThemes() {
      let areAnyDuplications = false

      let themeSlugs = []
      for (const themeValue of Object.values(this.shopSettings.Theme)) {
        themeSlugs.push(themeValue.slug)
      }
      areAnyDuplications = checkIfDuplicateExists(themeSlugs)
      if (areAnyDuplications) return 'Themes'

      let filtersSlugs = []
      for (const filterValue of Object.values(this.shopSettings.Filters)) {
        filtersSlugs.push(filterValue.slug)

        let filtersRowsSlugs = []
        for (const filterRowValue of Object.values(filterValue.rows)) {
          filtersRowsSlugs.push(filterRowValue.slug)
        }
        areAnyDuplications = checkIfDuplicateExists(filtersRowsSlugs)
        if (areAnyDuplications) return `Filter options: ${getDuplicatesFromArray(filtersRowsSlugs)}`
      }

      areAnyDuplications = checkIfDuplicateExists(filtersSlugs)

      return areAnyDuplications ? `Filters: ${getDuplicatesFromArray(filtersSlugs)}` : false
    },
    setSlugsForThemes() {
      for (const themeValue of Object.values(this.shopSettings.Theme)) {
        themeValue.slug = slugify(themeValue.name)
      }
      if (this.hasMultipleLanguages) this.setSlugsForThemesOnTranslations()
    },
    setSlugsForThemesOnTranslations() {
      if (typeof this.shopSettings.TranslationInfo !== 'undefined') {
        for (const translationValue of Object.values(
          this.shopSettings.TranslationInfo.Translations,
        )) {
          if (translationValue.Theme) {
            for (const [themeKey, themeValue] of Object.entries(translationValue.Theme)) {
              if (
                typeof translationValue.Theme[themeKey] !== 'undefined' &&
                typeof this.shopSettings.Theme[themeKey] !== 'undefined'
              ) {
                translationValue.Theme[themeKey].slug = this.shopSettings.Theme[themeKey].slug
              }
            }
          }
        }
      }
    },
    setSlugsForFilters() {
      for (const filterValue of Object.values(this.shopSettings.Filters)) {
        filterValue.slug = slugify(filterValue.name)

        for (const rowValue of Object.values(filterValue.rows)) {
          rowValue.slug = slugify(rowValue.name)
        }
      }
      if (this.hasMultipleLanguages) this.setSlugsForFiltersOnTranslations()
    },
    setSlugsForFiltersOnTranslations() {
      if (typeof this.shopSettings.TranslationInfo !== 'undefined') {
        for (const translationValue of Object.values(
          this.shopSettings.TranslationInfo.Translations,
        )) {
          if (translationValue.Filters) {
            for (const [filterKey, filterValue] of Object.entries(translationValue.Filters)) {
              if (
                typeof translationValue.Filters[filterKey] !== 'undefined' &&
                typeof this.shopSettings.Filters[filterKey] !== 'undefined'
              ) {
                translationValue.Filters[filterKey].slug = this.shopSettings.Filters[filterKey].slug
              }

              for (const [rowKey, rowValue] of Object.entries(filterValue.rows)) {
                if (
                  typeof translationValue.Filters[filterKey].rows[rowKey] !== 'undefined' &&
                  typeof this.shopSettings.Filters[filterKey].rows[rowKey] !== 'undefined'
                ) {
                  translationValue.Filters[filterKey].rows[rowKey].slug = this.shopSettings.Filters[
                    filterKey
                  ].rows[rowKey].slug
                }
              }
            }
          }
        }
      }
    },
  },
}
</script>
